import { createContext } from "react";

type HostListener = (hasOpaqueChrome: boolean) => void;

export type PrimaryControlsStripOpaqueChromeRegistry = {
  getCurrent: () => boolean;
  publish: () => () => void;
  subscribe: (listener: HostListener) => () => void;
};

/**
 * Tracks whether the focused screen paints its OWN opaque chrome directly beneath the app toolbar
 * — a pinned sticky accordion header, say — so the shared header fade in `NavLayout` can stand
 * down while it does.
 *
 * That fade exists to dissolve scrolling content passing under the toolbar. A pinned header is not
 * scrolling content: it never moves, so there is nothing to dissolve and the band only tints the
 * header itself. It visibly greyed the top of the deck's "Due Today" row and the Stats sections.
 *
 * Entries are REFERENCE COUNTED rather than a single boolean, because a navigation transition
 * mounts the incoming screen before unmounting the outgoing one. With a bare flag the leaver's
 * cleanup would clear a claim the arriver still holds, and the fade would flick back on over the
 * new screen's header.
 */
export function createPrimaryControlsStripOpaqueChromeRegistry(): PrimaryControlsStripOpaqueChromeRegistry {
  const entries = new Set<number>();
  let nextId = 0;
  const listeners = new Set<HostListener>();

  const getCurrent = (): boolean => entries.size > 0;

  const notify = () => {
    const current = getCurrent();
    for (const listener of listeners) {
      listener(current);
    }
  };

  return {
    getCurrent,
    publish() {
      const id = ++nextId;
      entries.add(id);
      notify();
      return () => {
        if (entries.delete(id)) {
          notify();
        }
      };
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

/**
 * Claiming opaque chrome is an enhancement to the shared header fade, never a requirement for a
 * component to render. A `SectionAccordionTab` mounted outside `NavLayout` — an isolated render
 * test, Storybook, the landscape sidebar shell — therefore falls back to this inert registry and
 * renders normally, rather than throwing the way the title/back contexts do. There is no fade to
 * suppress in those trees, so there is nothing for a missing provider to break.
 */
const INERT_REGISTRY: PrimaryControlsStripOpaqueChromeRegistry = {
  getCurrent: () => false,
  publish: () => () => undefined,
  subscribe: () => () => undefined,
};

export const PrimaryControlsStripOpaqueChromeContext =
  createContext<PrimaryControlsStripOpaqueChromeRegistry>(INERT_REGISTRY);
